import type { Product, ShoppingListItem } from '@recipes/contracts';
import {
  decideScanMatch,
  matchProductToItems,
  resolveHints,
  type MatchCandidate,
  type ScanDecision,
} from '@recipes/domain';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Stepper,
} from '@recipes/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, CheckCheck, ChevronDown, Plus, ScanBarcode, Tag, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { HintMarkerBadge, NutriScoreBadge } from '@/features/catalog/badges';
import {
  useCatalogHints,
  useCatalogProduct,
  useProductPending,
  useShoppingAt,
} from '@/features/catalog/hooks';
import { lookupBarcode, type ScanOutcome } from '@/features/catalog/lookup';
import { ProductCard } from '@/features/catalog/product-card';
import { ProductForm } from '@/features/catalog/product-form';
import { ScannerView } from '@/features/catalog/scanner-view';
import {
  formatMoney,
  priceHere,
  storeRefOf,
  useCurrency,
  useLearnedPairings,
  usePriceRules,
  usePricesEnabled,
  usePricesFor,
} from '@/features/prices/hooks';
import { PriceDialog } from '@/features/prices/price-dialog';
import { PriceChangeNote } from '@/features/prices/price-parts';
import { useProfile } from '@/features/shared/hooks';
import { toastError } from '@/lib/errors';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentLocale } from '@/lib/i18n';
import { offlineDb } from '@/lib/offline/db';
import { toBcp47 } from '@/lib/speech/recognition';
import { speak, ttsSupported } from '@/lib/tts/speak';
import { useShoppingPreferences } from './hooks';
import {
  addItemLocal,
  setCheckedLocal,
  setItemProductLocal,
  setItemPurchaseLocal,
} from './shopping-queue';

type Found = Extract<ScanOutcome, { status: 'found' }>;
const UNDO_MS = 5000;
const THIS_TRIP_MS = 12 * 3_600_000;

const isOpenOrBoughtOnThisTrip = (item: ShoppingListItem): boolean =>
  !item.checked || !item.checkedAt || Date.now() - Date.parse(item.checkedAt) < THIS_TRIP_MS;

const summaryOf = (product: Product): NonNullable<ShoppingListItem['product']> => ({
  id: product.id,
  name: product.name,
  brand: product.brand,
  barcode: product.barcode,
  nutriScore: product.nutriScore,
  thumbnailUrl: product.photos[0]?.thumbnailUrl ?? null,
});

/** Every name the product's ingredient goes by: the device copy knows the aliases. */
function useIngredientNames(product: Product): { names: string[]; ready: boolean } {
  const ingredientId = product.ingredient?.id ?? null;
  const row = useLiveQuery(
    async () =>
      ingredientId ? ((await offlineDb.catalogIngredients.get(ingredientId)) ?? null) : null,
    [ingredientId],
  );
  const names = useMemo(() => {
    const ingredient = row?.ingredient ?? null;
    const all = new Set<string>();
    if (product.ingredient) {
      all.add(product.ingredient.name);
      for (const name of Object.values(product.ingredient.names ?? {})) if (name) all.add(name);
    }
    for (const alias of ingredient?.aliases ?? []) all.add(alias.name);
    return [...all];
  }, [product.ingredient, row]);
  // Until the device copy answered, a match by alias may still be missing.
  return { names, ready: row !== undefined };
}

/**
 * One scanned product on top of the list (docs/partial/shopping-in-store-spec.md 5.2, 6.2): what
 * the household knows about it, which list item it belongs to, and the action that fits. The
 * match never creates anything by itself – and only an exact match may be checked off
 * automatically, always with Undo.
 */
function ScannedProduct({
  found,
  listId,
  items,
  assignTo,
  onNext,
}: {
  found: Found;
  listId: string;
  items: readonly ShoppingListItem[];
  /** Opened from an item's menu: the scanned product is assigned to this item. */
  assignTo?: ShoppingListItem | null;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const preferences = useShoppingPreferences();
  const { data: profile } = useProfile();
  const { store } = useShoppingAt();
  const learned = useLearnedPairings();
  const pricesEnabled = usePricesEnabled();
  const currency = useCurrency();
  const rules = usePriceRules();
  const [current, setCurrent] = useState<Product>(found.product);
  // How many were taken from the shelf (user, 2026-09-24): +/- next to the action, default 1.
  const [quantity, setQuantity] = useState(1);
  const stored = useCatalogProduct(current.id);
  const product = stored ?? current;
  const pending = useProductPending(product.id) ?? found.pending;
  const liveHints = useCatalogHints({
    productId: product.id,
    ingredientId: product.ingredient?.id ?? null,
  });
  const hints = liveHints.length ? liveHints : found.hints;
  const { names: ingredientNames, ready } = useIngredientNames(product);
  const prices = usePricesFor({ productId: product.id });
  const [more, setMore] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The decision is taken once per scan: checking an item off must not re-match the product
  // against the rest of the list while the result is on screen.
  const itemsAtScan = useRef(items);
  const candidates = useMemo(
    () =>
      matchProductToItems(
        {
          id: product.id,
          name: product.name,
          brand: product.brand,
          ingredientId: product.ingredient?.id ?? null,
          ingredientNames,
        },
        // "Already checked off" is about this trip. What was bought days ago and still sits in
        // Bought is no match: adding the product re-activates that entry (one add path).
        itemsAtScan.current.filter(isOpenOrBoughtOnThisTrip).map((item) => ({
          id: item.id,
          name: item.name,
          checked: item.checked,
          productId: item.product?.id ?? null,
          ingredientId: item.ingredientId ?? null,
          order: item.position,
        })),
        learned,
      ),
    [ingredientNames, learned, product],
  );
  const decision: ScanDecision = useMemo(
    () => decideScanMatch(candidates, preferences.scanCheckOff),
    [candidates, preferences.scanCheckOff],
  );
  const itemOf = (candidate: MatchCandidate) =>
    items.find((item) => item.id === candidate.itemId) ?? null;

  const say = useCallback(
    (text: string) => {
      setAnnouncement(text);
      if (!ttsSupported() || !(profile?.speechPreferences.readAloudEnabled ?? false)) return;
      void speak(text, {
        lang: toBcp47(currentLocale()),
        rate: profile?.speechPreferences.speechRate,
        voiceName: profile?.speechPreferences.voiceName,
      }).catch(() => undefined);
    },
    [profile],
  );

  const checkOff = useCallback(
    async (item: ShoppingListItem, automatic: boolean) => {
      setBusy(true);
      try {
        // A product captured a moment ago has no server id yet: the item is checked off, the
        // link follows with the next scan.
        await setCheckedLocal(item, true, pending ? undefined : product.id, summaryOf(product));
        if (quantity > 1) await setItemPurchaseLocal(item, { purchasedCount: quantity });
        setDone(item.id);
        say(t('shopping.scan.checkedOff', { name: item.name }));
        if (automatic)
          toast.show({
            title: t('shopping.scan.checkedOff', { name: item.name }),
            durationMs: UNDO_MS,
            action: {
              label: t('common.undo'),
              onAction: () => void setCheckedLocal(item, false).catch(toastError),
            },
          });
      } catch (error) {
        toastError(error);
      } finally {
        setBusy(false);
      }
    },
    [pending, product, quantity, say, t],
  );

  // AUTO applies to an exact match only – and exactly once per scan.
  const autoDone = useRef(false);
  useEffect(() => {
    if (!ready || autoDone.current || decision.kind !== 'PROPOSE' || !decision.auto) return;
    const item = items.find((candidate) => candidate.id === decision.candidate.itemId);
    if (!item || item.checked) return;
    autoDone.current = true;
    void checkOff(item, true);
  }, [checkOff, decision, items, ready]);

  // What the scan found is said once, for someone who is looking at the shelf.
  const announced = useRef(false);
  useEffect(() => {
    if (!ready || announced.current) return;
    announced.current = true;
    if (decision.kind === 'PROPOSE' && decision.auto) return;
    const item = 'candidate' in decision ? itemOf(decision.candidate) : null;
    if (decision.kind === 'PROPOSE' && item) say(t('shopping.scan.isOnList', { name: item.name }));
    else if (decision.kind === 'ALREADY_BOUGHT' && item)
      say(t('shopping.scan.alreadyBought', { name: item.name }));
    else if (decision.kind === 'OTHER_PRODUCT_BOUGHT' && item)
      say(t('shopping.scan.otherProductBought', { name: item.name, product: product.name }));
    else if (decision.kind === 'CHOOSE') say(t('shopping.scan.choose'));
    else say(t('shopping.scan.notOnList', { name: product.name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const add = async (check: boolean) => {
    setBusy(true);
    try {
      const result = await addItemLocal(listId, {
        // Next to the "Nudeln" that were bought as spaghetti, the gnocchi go by their own name.
        name:
          decision.kind === 'OTHER_PRODUCT_BOUGHT'
            ? product.name
            : (product.ingredient?.name ?? product.name),
        productId: pending ? undefined : product.id,
      });
      if (check) await checkOff(result.item, false);
      else {
        setDone(result.item.id);
        say(
          result.outcome === 'REACTIVATED'
            ? t('shopping.scan.reactivated', { name: result.item.name })
            : t('shopping.scan.added', { name: result.item.name }),
        );
      }
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  const assign = async (item: ShoppingListItem, check: boolean) => {
    setBusy(true);
    try {
      await setItemProductLocal(item, pending ? undefined : product.id, summaryOf(product));
      if (check) {
        setBusy(false);
        await checkOff(item, false);
        return;
      }
      setDone(item.id);
      say(t('shopping.scan.assigned', { name: item.name, product: product.name }));
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  const another = async (item: ShoppingListItem) => {
    setBusy(true);
    try {
      const count = (item.purchasedCount ?? 1) + quantity;
      await setItemPurchaseLocal(item, { purchasedCount: count });
      setDone(item.id);
      say(t('shopping.scan.boughtCount', { name: item.name, count }));
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  const here =
    store && pricesEnabled ? priceHere(prices, storeRefOf(store), currency, rules) : null;
  const marker = resolveHints(hints, {
    productId: product.id,
    ingredientId: product.ingredient?.id ?? null,
    storeId: store?.id ?? null,
    storeParentId: store?.parentStoreId ?? null,
  }).marker;
  const pack = product.packageQuantity
    ? `${product.packageQuantity} ${product.packageUnitCode ?? product.packageUnitText ?? ''}`.trim()
    : product.packageUnitText;
  const target = assignTo ? (items.find((i) => i.id === assignTo.id) ?? assignTo) : null;
  const proposed = decision.kind === 'PROPOSE' ? itemOf(decision.candidate) : null;
  const bought = decision.kind === 'ALREADY_BOUGHT' ? itemOf(decision.candidate) : null;
  const boughtAsOther =
    decision.kind === 'OTHER_PRODUCT_BOUGHT' ? itemOf(decision.candidate) : null;
  const canAdd =
    decision.kind === 'NONE' ||
    decision.kind === 'CHOOSE' ||
    decision.kind === 'OTHER_PRODUCT_BOUGHT';
  const addIsMain = decision.kind !== 'CHOOSE';
  const finished = done !== null;

  return (
    <div className="space-y-3" data-testid="scan-sheet-product">
      <p className="sr-only" role="status" aria-live="polite" data-testid="scan-sheet-announcement">
        {announcement}
      </p>

      {/* Compact card: what matters at the shelf; "More" opens the full one. */}
      <div className="rounded-lg border p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold">{product.name}</p>
            <p className="text-sm text-muted-foreground">
              {[product.brand, pack].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <HintMarkerBadge marker={marker} />
            {product.nutriScore && <NutriScoreBadge grade={product.nutriScore} size="sm" />}
          </div>
        </div>
        {found.conflicts.length > 0 && (
          <Alert variant="warning" className="mt-2" data-testid="scan-sheet-conflicts">
            {t('shopping.scan.conflicts', { count: found.conflicts.length })}
          </Alert>
        )}
        {pricesEnabled && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {here ? (
              <span className="tabular-nums" data-testid="scan-sheet-price">
                {here.current.stale && `${t('prices.approx')} `}
                {formatMoney(here.current.observation.price, currency)}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {store ? t('prices.noneHere') : t('prices.noStore')}
              </span>
            )}
            {here?.change?.noticeable && <PriceChangeNote change={here.change} compact />}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPriceOpen(true)}
              data-testid="scan-sheet-price-add"
            >
              <Tag aria-hidden /> {t('prices.action')}
            </Button>
          </div>
        )}
        <button
          type="button"
          className="mt-2 inline-flex min-h-[36px] items-center gap-1 text-sm underline"
          aria-expanded={more}
          onClick={() => setMore((value) => !value)}
          data-testid="scan-sheet-more"
        >
          <ChevronDown aria-hidden className="size-4" /> {t('shopping.scan.more')}
        </button>
        {more && (
          <div className="mt-2 border-t pt-2">
            <ProductCard
              product={product}
              hints={hints}
              conflicts={found.conflicts}
              store={store}
              pending={pending}
              onProductChanged={setCurrent}
            />
          </div>
        )}
      </div>

      {/* The match and its actions – at the bottom, where the thumb is. */}
      <div
        className="space-y-2"
        hidden={!ready}
        data-testid="scan-sheet-match"
        data-decision={decision.kind}
      >
        {finished && (
          <Alert data-testid="scan-sheet-done">{announcement || t('shopping.scan.done')}</Alert>
        )}

        {!finished && target && (
          <div className="space-y-2" data-testid="scan-sheet-assign">
            <p className="text-sm">{t('shopping.scan.assignTo', { name: target.name })}</p>
            <Button
              className="h-12 w-full"
              loading={busy}
              onClick={() => void assign(target, false)}
              data-testid="scan-sheet-assign-only"
            >
              <Check aria-hidden /> {t('shopping.scan.assign')}
            </Button>
            <Button
              variant="outline"
              className="h-12 w-full"
              disabled={busy}
              onClick={() => void assign(target, true)}
              data-testid="scan-sheet-assign-check"
            >
              <CheckCheck aria-hidden /> {t('shopping.scan.assignAndCheck')}
            </Button>
          </div>
        )}

        {!finished && !target && decision.kind !== 'NONE' && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">{t('shopping.scan.quantity')}</span>
            <Stepper
              value={quantity}
              onChange={setQuantity}
              min={1}
              max={99}
              label={t('shopping.scan.quantity')}
              decreaseLabel={t('shopping.scan.quantityLess')}
              increaseLabel={t('shopping.scan.quantityMore')}
              className="scan-quantity"
            />
          </div>
        )}

        {!finished && !target && proposed && (
          <>
            <p className="text-sm">{t('shopping.scan.isOnList', { name: proposed.name })}</p>
            <Button
              className="h-12 w-full"
              loading={busy}
              onClick={() => void checkOff(proposed, false)}
              data-testid="scan-sheet-check-off"
            >
              <Check aria-hidden /> {t('shopping.scan.checkOff', { name: proposed.name })}
            </Button>
          </>
        )}

        {!finished && !target && decision.kind === 'CHOOSE' && (
          <>
            <p className="text-sm">{t('shopping.scan.choose')}</p>
            <ul className="space-y-2">
              {decision.candidates.map((candidate) => {
                const item = itemOf(candidate);
                if (!item) return null;
                return (
                  <li key={candidate.itemId}>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-start"
                      disabled={busy}
                      onClick={() => void checkOff(item, false)}
                      data-testid="scan-sheet-candidate"
                    >
                      <Check aria-hidden /> {item.display ? `${item.display} ` : ''}
                      {item.name}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {!finished && !target && bought && (
          <>
            <p className="text-sm">{t('shopping.scan.alreadyBought', { name: bought.name })}</p>
            <Button
              variant="outline"
              className="h-12 w-full"
              loading={busy}
              onClick={() => void another(bought)}
              data-testid="scan-sheet-another"
            >
              <Plus aria-hidden /> {t('shopping.scan.another')}
            </Button>
          </>
        )}

        {!finished && !target && canAdd && (
          <div className="grid gap-2 sm:grid-cols-2">
            {boughtAsOther && (
              <p className="text-sm sm:col-span-2" data-testid="scan-sheet-other-product">
                {t('shopping.scan.otherProductBought', {
                  name: boughtAsOther.name,
                  product: product.name,
                })}
                {boughtAsOther.product?.name &&
                  ` ${t('shopping.scan.otherProductWas', { other: boughtAsOther.product.name })}`}
              </p>
            )}
            {decision.kind === 'NONE' && (
              <p className="text-sm sm:col-span-2">
                {t('shopping.scan.notOnList', { name: product.name })}
              </p>
            )}
            <Button
              variant={addIsMain ? 'default' : 'ghost'}
              className="h-12"
              disabled={busy}
              onClick={() => void add(true)}
              data-testid="scan-sheet-add-checked"
            >
              <CheckCheck aria-hidden /> {t('shopping.scan.addAndCheckOff')}
            </Button>
            <Button
              variant={addIsMain ? 'outline' : 'ghost'}
              className="h-12"
              disabled={busy}
              onClick={() => void add(false)}
              data-testid="scan-sheet-add"
            >
              <Plus aria-hidden /> {t('shopping.scan.addToList')}
            </Button>
          </div>
        )}

        <Button
          variant="ghost"
          className="h-12 w-full"
          onClick={onNext}
          data-testid="scan-sheet-next"
        >
          <ScanBarcode aria-hidden /> {t('shopping.scan.next')}
        </Button>
      </div>

      <PriceDialog
        open={priceOpen}
        onOpenChange={setPriceOpen}
        target={{
          productId: product.id,
          name: product.brand ? `${product.brand} ${product.name}` : product.name,
          packageQuantity: product.packageQuantity,
          packageUnitCode: product.packageUnitCode,
        }}
        defaultStoreId={store?.id ?? null}
      />
    </div>
  );
}

/**
 * The scanner as a sheet over the list – not a page change. Lookup, match, check-off, add and
 * the capture of an unknown product run on the device copy and its queues, so all of it works
 * in flight mode.
 */
export function ScanSheet({
  open,
  onOpenChange,
  listId,
  items,
  initialBarcode,
  assignTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  items: readonly ShoppingListItem[];
  /** Tests and deep links start with a code instead of the camera. */
  initialBarcode?: string | null;
  /** Opened from an item's menu: what is scanned becomes that item's product. */
  assignTo?: ShoppingListItem | null;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [scanKey, setScanKey] = useState(0);

  const onCode = useCallback(async (barcode: string) => {
    setBusy(true);
    try {
      setOutcome(await lookupBarcode(barcode));
      setScanKey((key) => key + 1);
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setOutcome(null);
      return;
    }
    if (initialBarcode) void onCode(initialBarcode);
  }, [initialBarcode, onCode, open]);

  const next = () => setOutcome(null);
  // On a phone the sheet is a panel at the bottom that leaves the list above it usable: no
  // backdrop, no focus trap, the camera window only as tall as the scan frame (user, 2026-09-24).
  const desktop = useMediaQuery('(min-width: 640px)');

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={desktop}>
      <DialogContent
        closeLabel={t('shopping.scan.doneAction')}
        className="inset-x-0 bottom-0 left-0 top-auto max-h-[62dvh] translate-x-0 translate-y-0 overflow-y-auto rounded-b-none rounded-t-xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[94dvh] sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:p-6"
        onInteractOutside={(event) => {
          if (!desktop) event.preventDefault();
        }}
        data-testid="scan-sheet"
      >
        <DialogHeader className={desktop ? undefined : 'sr-only'}>
          <DialogTitle>{t('shopping.scan.title')}</DialogTitle>
          <DialogDescription>{t('shopping.scan.description')}</DialogDescription>
        </DialogHeader>
        {assignTo && (
          <p className="text-sm font-medium" data-testid="scan-sheet-assign-hint">
            {t('shopping.scan.assignHint', { name: assignTo.name })}
          </p>
        )}
        {!online && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs"
          >
            <WifiOff className="size-4" aria-hidden /> {t('shopping.scan.offline')}
          </p>
        )}

        {(outcome === null || outcome.status === 'invalid') && (
          <ScannerView onCode={(code) => void onCode(code)} paused={busy} compact={!desktop} />
        )}
        <div aria-live="polite">
          {busy && <p className="text-sm text-muted-foreground">{t('catalog.scan.lookingUp')}</p>}
          {outcome?.status === 'invalid' && (
            <Alert variant="destructive">{t('catalog.scanner.invalid')}</Alert>
          )}
        </div>

        {outcome?.status === 'found' && (
          <ScannedProduct
            key={`${outcome.product.id}-${scanKey}`}
            found={outcome}
            listId={listId}
            items={items}
            assignTo={assignTo}
            onNext={next}
          />
        )}

        {outcome?.status === 'unknown' && (
          <div className="space-y-3" data-testid="scan-sheet-unknown">
            <h3 className="font-semibold">{t('catalog.scan.unknownTitle')}</h3>
            <p className="text-sm text-muted-foreground">
              {outcome.offline ? t('catalog.scan.unknownOffline') : t('catalog.scan.unknownBody')}
            </p>
            <ProductForm
              barcode={outcome.barcode}
              onCancel={next}
              onSaved={(product) => {
                // The match step runs with the product that was just captured.
                setOutcome({
                  status: 'found',
                  product,
                  hints: [],
                  conflicts: [],
                  origin: 'DEVICE',
                  pending: true,
                });
                setScanKey((key) => key + 1);
              }}
            />
          </div>
        )}

        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          data-testid="scan-sheet-close"
        >
          {t('shopping.scan.doneAction')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
